


/**
 * Style Hub (Style & PO) — Jay's skeleton, built in the portal. Named "S&P Hub" until
 * 2026-09-08; the function key is still sp_hub.
 *
 * The Hub is a reconciliation view over three ledgers that are never merged (design decision D1/D2):
 * SAP SO lines, SAP PO lines and NetSuite PO lines, tied together by style_master + code_crosswalk.
 * Of the three sources only NetSuite is cloud-hosted, so the Connections tab exists to make the state of
 * each integration visible - it is also what was promised to Hyun Kim (SAP/IT) so he can run the SAP
 * connection test himself instead of asking for shell access to this server.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Plug, Database, AlertTriangle, CheckCircle2, MinusCircle, Download } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import i18n from './i18n';
import Header from './Header';
import TabIcon from './TabIcon';
// The Sample Tracker's column menu, not SPLedgerHeader: that one fetches its value list per column from the
// server because the ledger tables hold 20 rows out of 30,000. The style master is 4,185 rows and is loaded
// whole, so sorting and filtering happen in the browser with no round trip at all.
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import { BACKEND_URL } from './backendConfig';
import useRowWindow from './useRowWindow';
import ModuleHelp from './ModuleHelp';
import './SPHub.css';

const API_BASE = `${BACKEND_URL}/api/sp`;

/*
 * 'sap' (SAP Connection Test) and 'season' (Season & Buy#) were removed from this list on 2026-08-27 at
 * Max's request. Both sections and both endpoints are still here and still work — only the tabs are gone —
 * so putting either back is one line in this array.
 *
 * Why they could go:
 *   SAP Connection Test is a five-stage diagnostic built so Hyun Kim could check SAP himself. The
 *   Connections card now carries the same verdict in one line ("Logged on. sysId=PS4 … 10 of 10 tables
 *   readable"), and the extra stages only earn their place when SAP is BROKEN — see the caveat in the
 *   handover note.
 *
 *   Season & Buy# has never really been used: of the 7,124 changes to those two fields, 7,116 came from the
 *   `mastersheet` bulk import on 2026-08-16 and the remaining 8 are Max's own tests from 08-10. The values
 *   themselves stay visible on Hub View (Season and Buy_Cycle are two of its eighteen columns) and on
 *   Ledgers (raw), so removing the tab hides the editor, not the data.
 */
/* THE GUIDE (Max 01/11: every module has one). It opens on Connections; the other tabs are introduced by the tab-row
   step rather than pointed at, because their tables are only drawn after a click. */
const TOUR = [
  { selector: '.sp-tabs', title: 'tourSpHub.tabsT', body: 'tourSpHub.tabsB' },
  { selector: '.sp-conn-grid', title: 'tourSpHub.connT', body: 'tourSpHub.connB' },
  { selector: '.sp-refresh', title: 'tourSpHub.refreshT', body: 'tourSpHub.refreshB' },
  { selector: '.sp-refresh .sp-head-btns', title: 'tourSpHub.btnsT', body: 'tourSpHub.btnsB' },
];

const TABS = [
  { key: 'connections', labelKey: 'xHub.tabs.connections' },
  { key: 'style', labelKey: 'xHub.tabs.style' },
  /* Hub View, Ledgers (raw), Recon, Vendor Map and Price Table were removed on 2026-08-28. Max talked to
     Jay: those screens came out of Jay's AI-drafted skeleton and nobody reviews them, so keeping five tabs
     of unread reconciliation UI was cost without a reader. The DATA all stays — the hub_view DB view, the
     recon view, the vendor map and the price lines tables are untouched, the vendor map still names the
     Master Sheet's factories, and the backend endpoints still answer — so any of the screens can come back
     by reverting this commit if someone starts needing one. Master Sheet lives at
     /production/master-sheet (src/28_MasterSheet.jsx). */
  { key: 'material', labelKey: 'xHub.tabs.material' },
];



const STATE_META = {
  ok: { labelKey: 'xHub.state.connected', cls: 'sp-pill ok', Icon: CheckCircle2 },
  error: { labelKey: 'xHub.state.failed', cls: 'sp-pill err', Icon: AlertTriangle },
  'not-configured': { labelKey: 'xHub.state.notConfigured', cls: 'sp-pill idle', Icon: MinusCircle },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh', hour12: false });
  } catch (error) {
    return iso;
  }
};

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`sp-stat ${tone || ''}`}>
      <div className="sp-stat-value">{value === undefined || value === null ? '—' : Number(value).toLocaleString('en-GB')}</div>
      <div className="sp-stat-label">{label}</div>
      {sub && <div className="sp-stat-sub">{sub}</div>}
    </div>
  );
}

function ConnectionCard({ source, credentialsElsewhere }) {
  const { t } = useTranslation();
  // "Not configured" reads as somebody forgot to set this up. On a server that deliberately holds no
  // credentials nothing is misconfigured, so the card says where it does run instead — and drops the list of
  // missing keys, which is a to-do list for a problem that does not exist here.
  const meta = credentialsElsewhere
    ? { labelKey: 'xHub.state.scheduler', cls: 'sp-pill mute', Icon: MinusCircle }
    : (STATE_META[source.state] || STATE_META['not-configured']);
  const { Icon } = meta;
  return (
    <div className={`sp-conn-card ${credentialsElsewhere ? 'scheduled' : source.state}`}>
      <div className="sp-conn-head">
        <div className="sp-conn-name">{source.name}</div>
        <span className={meta.cls}><Icon size={12} /> {t(meta.labelKey)}</span>
      </div>
      <div className="sp-conn-row"><span>{t('xHub.conn.transport')}</span><strong>{source.transport}</strong></div>
      {source.host && <div className="sp-conn-row"><span>{t('xHub.conn.host')}</span><strong>{source.host}</strong></div>}
      {source.accountId && <div className="sp-conn-row"><span>{t('xHub.conn.account')}</span><strong>{source.accountId}</strong></div>}
      {Number.isFinite(source.ms) && source.state === 'ok' && (
        <div className="sp-conn-row"><span>{t('xHub.conn.roundTrip')}</span><strong>{source.ms} ms</strong></div>
      )}
      {!credentialsElsewhere && !!source.missingKeys?.length && (
        <div className="sp-conn-missing">
          {t('xHub.conn.waitingFor', { keys: source.missingKeys.join(', ') })}
        </div>
      )}
      {/* Shown ALONGSIDE the missing keys, not instead of them. Hiding it lost the distinction that matters
          most on this screen: PLM waiting on an account with the network already open reads very differently
          from PLM being unreachable, and only the detail line says which. */}
      {credentialsElsewhere ? (
        <div className="sp-conn-detail">{t('xHub.conn.scheduled')}</div>
      ) : source.detail && (
        // Raw provider message on purpose - a summarised error hides whether it is auth, network or data.
        <div className={`sp-conn-detail ${source.state === 'error' ? 'err' : ''}`}>{source.detail}</div>
      )}
    </div>
  );
}

// A refresh is expected twice a day, so a ledger untouched for more than 26 hours has missed at least two
// runs — one late run is not worth an alarm, two is. Deliberately not "since midnight": a run at 05:30
// would flip the whole screen to stale at 00:00 while the data was six hours from being replaced anyway.
const SP_STALE_HOURS = 26;

const hoursSince = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isFinite(ms) ? ms / 3600000 : null;
};

// "3 hours ago" answers the freshness question that a timestamp makes the reader compute themselves.
// Called while rendering, so the shared instance reads the language on screen.
const fmtAge = (iso) => {
  const h = hoursSince(iso);
  if (h === null) return '';
  if (h < 1) return i18n.t('xHub.age.min', { n: Math.max(1, Math.round(h * 60)) });
  if (h < 48) return i18n.t('xHub.age.hours', { n: Math.round(h) });
  return i18n.t('xHub.age.days', { n: Math.round(h / 24) });
};

function RefreshRow({ ledger, showProblem }) {
  const { t } = useTranslation();
  const success = ledger.lastSuccess;
  const attempt = ledger.lastAttempt;
  const stale = !success || (hoursSince(success.startedAt) ?? 999) > SP_STALE_HOURS;
  // A failure AFTER the last success is the case worth shouting about: the rows on screen are fine, but
  // they have stopped being replaced, and nothing else on this page would say so.
  const failedSince = attempt && !attempt.ok
    && (!success || new Date(attempt.startedAt) > new Date(success.startedAt));

  // Its own map rather than STATE_META: that one describes whether a connection is configured, which is a
  // different question, and it has no 'stale' state to borrow.
  let meta = { labelKey: 'xHub.state.upToDate', cls: 'sp-pill ok', Icon: CheckCircle2 };
  if (failedSince) meta = { labelKey: 'xHub.state.lastRunFailed', cls: 'sp-pill err', Icon: AlertTriangle };
  else if (stale) meta = { labelKey: 'xHub.state.stale', cls: 'sp-pill idle', Icon: MinusCircle };
  const { Icon } = meta;
  const label = t(meta.labelKey);

  return (
    <tr className={failedSince ? 'sp-rf-bad' : ''}>
      <td><strong>{ledger.name}</strong></td>
      <td className="sp-rf-num">{ledger.rows === null ? '—' : Number(ledger.rows).toLocaleString('en-GB')}</td>
      <td>
        {success ? fmtTime(success.startedAt) : t('xHub.refresh.never')}
        {success && <span className="sp-rf-age">{fmtAge(success.startedAt)}</span>}
      </td>
      <td>
        {success
          ? t('xHub.refresh.result', {
            read: Number(success.read ?? 0).toLocaleString('en-GB'),
            inserted: success.inserted ?? 0,
            updated: success.updated ?? 0,
          })
          : '—'}
      </td>
      <td><span className={meta.cls}><Icon size={12} /> {label}</span></td>
      {/* The raw message, same reasoning as the connection cards: a summarised error hides whether SAP was
          unreachable, the credentials were rejected, or the script refused to write. */}
      {showProblem && (
        <td className="sp-rf-err">{failedSince ? `${fmtAge(attempt.startedAt)}: ${attempt.error || t('xHub.refresh.failed')}` : ''}</td>
      )}
    </tr>
  );
}

function RefreshPanel({ status, onReload, loading, canRefresh, manual, onRefreshSource }) {
  const { t } = useTranslation();
  // The Problem column is dropped entirely when everything is healthy, which is most days — an always-empty
  // column reads as something that failed to load rather than as good news.
  const showProblem = useMemo(() => (status?.ledgers || []).some((l) => {
    const a = l.lastAttempt;
    const sOk = l.lastSuccess;
    return a && !a.ok && (!sOk || new Date(a.startedAt) > new Date(sOk.startedAt));
  }), [status]);
  if (!status) return null;
  return (
    <div className="sp-refresh">
      <div className="sp-section-head">
        <div>
          <Database size={14} /> {t('xHub.refresh.head')}
          <span className="sp-sub">{status.schedule}</span>
        </div>
        <div className="sp-head-btns">
          {/* One button per SOURCE, not per ledger: "SAP" is two ledgers and nobody thinks of them as two
              jobs. The old manual sync lived on the Ledgers tab and followed that tab's dropdown, which is
              why it could not be found (Max, 2026-08-27). */}
          {canRefresh && (
            <>
              <button type="button" className="sp-btn" disabled={!!manual.step}
                onClick={() => onRefreshSource('sap')}>
                <RefreshCw size={13} /> {manual.kind === 'sap' ? t('xHub.refresh.stepRunning', { step: manual.step }) : t('xHub.refresh.sapNow')}
              </button>
              <button type="button" className="sp-btn" disabled={!!manual.step}
                onClick={() => onRefreshSource('ns')}>
                <RefreshCw size={13} /> {manual.kind === 'ns' ? t('xHub.refresh.stepRunning', { step: manual.step }) : t('xHub.refresh.nsNow')}
              </button>
            </>
          )}
          <button type="button" className="sp-btn" onClick={onReload} disabled={loading}>
            <RefreshCw size={13} /> {loading ? t('xHub.refresh.checking') : t('xHub.refresh.checkAgain')}
          </button>
        </div>
      </div>
      <p className="sp-note">{t('xHub.refresh.note')}</p>
      {manual.error && <div className="sp-alert err">{manual.error}</div>}
      {manual.done && <div className="sp-alert ok">{t('xHub.refresh.done', { done: manual.done })}</div>}
      <div className="sp-table-wrap">
        <table className="sp-table sp-rf-table">
          <thead>
            <tr>
              <th>{t('xHub.refresh.colLedger')}</th><th className="sp-rf-num">{t('xHub.refresh.colRows')}</th>
              <th>{t('xHub.refresh.colLast')}</th>
              <th>{t('xHub.refresh.colResult')}</th><th>{t('xHub.refresh.colState')}</th>
              {showProblem && <th>{t('xHub.refresh.colProblem')}</th>}
            </tr>
          </thead>
          <tbody>
            {status.ledgers.map((l) => <RefreshRow key={l.key} ledger={l} showProblem={showProblem} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// One place for the nine columns: the header, the getters the filter menu reads, and the cell renderers all
// walk this list, so adding a column cannot leave the three out of step.
/* Headers are the labels SAP prints above these fields, not the field names (Max, 2026-09-08:
   "header lấy theo tên header của nguồn chứ ko phải lấy tên nguồn" — a correction of the first pass,
   which read MATNR / MAKTX / PRDHA). They are not written by hand: they are DD04T.REPTEXT for each
   field's data element, in English, read with scripts/sap_field_labels.mjs. So the header here is
   the same string a person sees above the column in SAP.

   FERTH's label is the one that explains the column: "Douzone material" (더존자재번호). Douzone is
   the Korean ERP Helinox ran before SAP, so this is the pre-SAP material number — which is exactly
   what everyone here has been calling the selling SKU, and why FERTH agrees with the PLM export's
   `legacy_no` on 98.7% of rows. They are two copies of the same number; SAP's is the authoritative one.

   Line and Category are two levels decoded out of ONE field, so they share PRDHA's label with the
   level in brackets rather than pretending to be two different fields.

   Season is the one column allowed to come from an import. Status is gone: the loader wrote the
   literal string 'Active' into all 8,703 rows, so it never carried information, only the look of it. */
const STYLE_COLUMNS = [
  { key: 'style_key', label: 'Material', width: 100 },
  { key: 'sku', label: 'Douzone material', width: 150 },
  { key: 'maktx', label: 'Material Description', width: 250 },
  { key: 'line_name', label: 'Product hierarchy (Line)', width: 192 },
  { key: 'product_category', label: 'Product hierarchy (Category)', width: 222 },
  { key: 'material_type', label: 'MTyp', width: 74 },
  { key: 'material_group', label: 'Matl Group', width: 110 },
  { key: 'color', label: 'Prod.color', width: 150 },
  { key: 'barcode', label: 'Barcode', width: 126 },
  { key: 'plm_season', label: 'Season', width: 88 },
  /* SFP Code, SFP Description and BOM codes removed 2026-09-08 (Max: "ko cần 3 cột"). They were the
     only columns here not read live from SAP — built from a ZPPR0030 export last loaded on 5 August,
     which is why BOM codes read 1 for all 4,847 styles created since: not "one part", but "no BOM data
     yet". A number that means two different things depending on when the row was created is worse than
     no number. The codes are still searchable; they just have no column.
     PLM No. went the same way earlier: filled on 328 of 4,185 styles, a near-empty strip that read as
     broken. Both are still in the API for whoever wants them back. */
];
// code_count is a number and must sort as one — as text, 10 sorts before 3.
/* Widths are declared, not left to the browser. This table only renders the ~20 rows in view, so an
   automatic layout re-measured the columns against a different set of values on every scroll step and
   the whole table shifted under the cursor (Max, 2026-09-08: "các cột nó ko fix width á nên scroll
   down nó bị di chuyển"). A fixed layout plus a colgroup is the only thing that makes a windowed
   table hold still — the same fix Product Line and the ticket table already needed. */
/* Each width is the header's own measured requirement, not a guess: label scrollWidth + the funnel
   button + the cell padding, read out of the rendered page (Max, 2026-09-08: "tăng width cột ... để
   thấy rõ icon sort & filter"). Six columns were short, not the three he could see — Matl Group by 5px,
   BOM codes by 14, Season by 1 — and a squeezed funnel looks like a missing one. */
const STYLE_NO_W = 46;
const STYLE_TABLE_W = STYLE_NO_W + STYLE_COLUMNS.reduce((sum, c) => sum + c.width, 0);

/* Material Master, given the same treatment as Style Master (Max, 2026-09-08): a No column, no pager,
   20 rows and a scroll, Excel-style sort/filter menus and an Excel export. Widths are declared for the
   same reason as the style table — only the rows in view exist, so an automatic layout re-measures them
   on every scroll step. */
/* Unlike the style table, these headings are the portal's own words, so they follow the screen's language
   (labelKey, translated where drawn and in the export). */
const MATERIAL_COLUMNS = [
  { key: 'material_code', labelKey: 'xHub.material.col.sapCode', width: 110 },
  // No pill on Type any more: Max asked for it to read like every other cell. It was a badge for a
  // value that is neither a status nor an action, which is what a pill is for.
  { key: 'material_type', labelKey: 'xHub.material.col.type', width: 84 },
  // name_en is what SAP holds; on some rows that still carries a Korean status note such as
  // "(생산금지)", which is SAP's own text and is left as it is.
  { key: 'name_en', labelKey: 'xHub.material.col.nameEn', width: 260 },
  { key: 'line_category', labelKey: 'xHub.material.col.lineCategory', width: 240 },
  { key: 'legacy_no', labelKey: 'xHub.material.col.legacy', width: 124 },
  { key: 'plm_material_no', labelKey: 'xHub.material.col.plm', width: 190 },
  { key: 'base_uom', labelKey: 'xHub.material.col.uom', width: 76 },
  { key: 'barcode', labelKey: 'xHub.material.col.barcode', width: 140 },
];
const MATERIAL_NO_W = 46;
const MATERIAL_TABLE_W = MATERIAL_NO_W + MATERIAL_COLUMNS.reduce((sum, c) => sum + c.width, 0);
const MATERIAL_GETTERS = Object.fromEntries(
  MATERIAL_COLUMNS.map((c) => [c.key, (row) => row[c.key] || '']));

/* Line / Category is two columns of SAP's joined for display, so it has no field of its own to sort or
   filter on. Deriving it once, as the rows arrive, gives the funnel something real to list — computing
   it inside the cell would have left the menu empty. */
const withDerived = (rows) => (rows || []).map((r) => ({
  ...r,
  name_en: r.name_en || r.name_ko || '',
  line_category: (r.line_name || r.product_category)
    ? `${r.line_name || '—'} / ${r.product_category || '—'}`
    : (r.product_hierarchy || ''),
}));

const STYLE_GETTERS = Object.fromEntries(STYLE_COLUMNS.map((c) => [
  c.key,
  c.key === 'code_count' ? (row) => Number(row.code_count || 0) : (row) => row[c.key] || '',
]));

export default function SPHub() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('connections');
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [style, setStyle] = useState(null);
  // Every style in one request. 4,185 rows is a payload the browser can hold, and holding it is what lets the
  // column menus list the values that are actually there — a server-paged table can only offer the current
  // page's values, which is the trap SPLedgerHeader exists to work around.
  const [styleQuery, setStyleQuery] = useState({ noSeason: false });
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [styleSort, setStyleSort] = useState({ key: '', dir: 'asc' });
  // { fileName, contentBase64 } for the file picked, and the server's dry-run summary of it.
  const [seasonFile, setSeasonFile] = useState(null);
  const [seasonPreview, setSeasonPreview] = useState(null);
  const [seasonBusy, setSeasonBusy] = useState(false);
  const [seasonError, setSeasonError] = useState('');
  const [styleFilters, setStyleFilters] = useState({});
  const [styleOpenFilter, setStyleOpenFilter] = useState(null);
  const [material, setMaterial] = useState(null);
  // One request for the whole table — no page, no server-side search or type filter. Everything the
  // toolbar and the column menus do now happens over the same in-memory array, so they compose.
  const [materialSort, setMaterialSort] = useState({ key: '', dir: 'asc' });
  const [materialFilters, setMaterialFilters] = useState({});
  const [materialOpenFilter, setMaterialOpenFilter] = useState(null);
  const [materialType, setMaterialType] = useState('');
  // What is typed, before it is committed — the list is 14k rows, so it searches on Enter, not per keystroke.
  const [materialSearch, setMaterialSearch] = useState('');

  // The manual refresh on the Connections tab. Separate from `sync`, which belongs to the Ledgers tab and
  // follows that tab's ledger dropdown — which is exactly why nobody could find a refresh button.
  const [manual, setManual] = useState({ kind: '', step: '', error: '', done: '' });
  const [docs, setDocs] = useState(null);
  const [docQuery, setDocQuery] = useState({ kind: 'po', year: '2026', q: '', missing: false });
  const [docSel, setDocSel] = useState([]);            // document numbers ticked for a bulk set
  const [docForm, setDocForm] = useState({ season: '', buyCycle: '', saving: false, error: '', done: '' });
  // Editing straight in the cell. The toolbar handles a batch, but a Season column showing "—" reads as
  // something you type into, so it has to actually be one — Max went looking for exactly that.
  const [cellEdit, setCellEdit] = useState(null);      // { docNo, field, value, saving, error }

  const loadConnections = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/connections`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || i18n.t('xHub.failedStatus', { status: response.status }));
      setConn(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRefreshStatus = useCallback(async () => {
    setRefreshLoading(true);
    try {
      const response = await fetch(`${API_BASE}/refresh-status`, { credentials: 'include' });
      if (response.ok) setRefreshStatus(await response.json());
    } catch (err) {
      // Freshness is informational; a failure here must not blank the connection cards below it.
    } finally {
      setRefreshLoading(false);
    }
  }, []);

  /* loadSummary went with the three stat cards it fed — they were its only reader, so the page no
     longer asks /ledger/summary on every load. */
  useEffect(() => {
    loadConnections();
    loadRefreshStatus();
  }, [loadConnections, loadRefreshStatus]);


  const loadStyle = useCallback(async (query) => {
    try {
      const params = new URLSearchParams({ page: 1, pageSize: 5000 });
      if (query.noSeason) params.set('noSeason', '1');
      const response = await fetch(`${API_BASE}/style-master?${params}`, { credentials: 'include' });
      let data = await response.json();
      if (!response.ok) return;
      // The whole set has to be in the browser or the column menus offer only the values on this page --
      // that is the entire reason it is not server-paged. One row per style per SEASON took the count past
      // the first request's 5,000, so it now asks again for exactly what it was told is there rather than
      // quietly rendering a truncated table.
      if (data.total > data.rows.length) {
        params.set('pageSize', String(data.total));
        const rest = await fetch(`${API_BASE}/style-master?${params}`, { credentials: 'include' });
        if (rest.ok) data = await rest.json();
      }
      setStyle(data);
    } catch (err) {
      // keep the previous result rather than blanking the table
    }
  }, []);

  useEffect(() => { if (tab === 'style') loadStyle(styleQuery); }, [tab, styleQuery, loadStyle]);

  /**
   * One call for both the dry run and the write, because the preview a person approves has to be produced by
   * exactly the code that then does the writing. The file is sent as base64 and parsed only on the server:
   * the NFC import has a parser in the browser and another on the server, and they drifted a column apart
   * for twelve days before anyone noticed.
   */
  const runSeasonImport = useCallback(async (apply) => {
    if (!seasonFile) return;
    setSeasonBusy(true);
    setSeasonError('');
    try {
      const response = await fetch(`${API_BASE}/style-master/season-import`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...seasonFile, apply }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || data.error || i18n.t('xHub.failedStatus', { status: response.status }));
      setSeasonPreview(data);
      if (data.applied) loadStyle(styleQuery);
    } catch (err) {
      setSeasonError(err.message);
      setSeasonPreview(null);
    } finally {
      setSeasonBusy(false);
    }
  }, [seasonFile, loadStyle, styleQuery]);

  const pickSeasonFile = useCallback((file) => {
    setSeasonPreview(null);
    setSeasonError('');
    if (!file) { setSeasonFile(null); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      setSeasonFile({ fileName: file.name, contentBase64: result.slice(result.indexOf(',') + 1) });
    };
    reader.onerror = () => setSeasonError(i18n.t('xHub.style.readError'));
    reader.readAsDataURL(file);
  }, []);


  const styleRows = useMemo(() => {
    const all = style?.rows || [];
    const kept = Object.keys(styleFilters).length
      ? all.filter((r) => rowPassesColumnFilters(r, styleFilters, STYLE_GETTERS))
      : all;
    return styleSort.key ? sortRows(kept, styleSort, STYLE_GETTERS) : kept;
  }, [style, styleFilters, styleSort]);

  /* Every row here is 32px — measured, and true because every cell is one nowrap line. Same hook as
     Product Line: two tables with the same problem should not carry two copies of the answer. */
  /* Export what is on screen, not the whole table: the point of the funnels and the "No season yet"
     tick is to narrow 10,529 rows down to a question, and an export that ignores them answers a
     different one. The row count and the active filters go into the sheet's own header so the file
     still says what it is a month later.
     Reuses the Sample Tracker's generic xlsx endpoint, the same way Product Line and Ticket Codes do —
     the column widths travel with it so the sheet opens readable rather than at Excel's default 8.43. */
  const exportStyleExcel = async () => {
    setXlsxBusy(true); setError('');
    try {
      const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
      const filename = `HEVN_Style_Master_${stamp.replace(/[: ]/g, '-')}.xlsx`;
      const active = Object.entries(styleFilters)
        .map(([key, values]) => `${STYLE_COLUMNS.find((c) => c.key === key)?.label || key}: ${[...values].join(', ')}`);
      const res = await fetch(`${BACKEND_URL}/api/development/sample-tracker/export-xlsx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          filename,
          title: t('xHub.style.expTitle'),
          generated: stamp,
          sheetName: t('xHub.style.expSheet'),
          filters: [
            t('xHub.common.rowsOf', { n: styleRows.length, total: (style?.rows || []).length }),
            styleQuery.noSeason ? t('xHub.style.noSeason') : '',
            ...active,
          ].filter(Boolean),
          columns: STYLE_COLUMNS.map((c) => c.label),
          // Excel counts characters, the table counts pixels — roughly 7px a character at this size.
          widths: STYLE_COLUMNS.map((c) => Math.max(10, Math.round(c.width / 7))),
          freezeColumns: 3,
          // The endpoint reads each row as { values: [...] }; a bare array produced empty rows.
          rows: styleRows.map((r) => ({ values: STYLE_COLUMNS.map((c) => r[c.key] ?? '') })),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || t('xHub.common.exportFailed', { status: res.status }));
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(e.message); } finally { setXlsxBusy(false); }
  };

  /* Search, the type box and the column funnels all narrow the SAME array, in that order, so ticking a
     funnel while a search is live means both — which is what a person expects and what the server-side
     version could not do. */
  const materialRows = useMemo(() => {
    const all = material?.rows || [];
    const q = materialSearch.trim().toLowerCase();
    let kept = all;
    if (materialType) kept = kept.filter((r) => r.material_type === materialType);
    if (q) {
      // The same fields the server used to search: every code a person might paste in identifies the
      // same material to a different team.
      kept = kept.filter((r) => ['material_code', 'name_en', 'name_ko', 'legacy_no', 'plm_material_no', 'barcode']
        .some((f) => String(r[f] || '').toLowerCase().includes(q)));
    }
    if (Object.keys(materialFilters).length) {
      kept = kept.filter((r) => rowPassesColumnFilters(r, materialFilters, MATERIAL_GETTERS));
    }
    return materialSort.key ? sortRows(kept, materialSort, MATERIAL_GETTERS) : kept;
  }, [material, materialSearch, materialType, materialFilters, materialSort]);

  const materialTh = (column) => ({
    label: t(column.labelKey),
    sortKey: column.key,
    sort: materialSort,
    onSort: (key, dir) => setMaterialSort((prev) => ({
      key,
      dir: dir || (prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'),
    })),
    filterValues: materialOpenFilter?.key === column.key
      ? distinctColumnValues(material?.rows || [], column.key, materialFilters, MATERIAL_GETTERS)
      : undefined,
    filterSelected: materialFilters[column.key],
    filterOpen: materialOpenFilter?.key === column.key,
    filterAnchor: materialOpenFilter?.key === column.key ? materialOpenFilter.anchor : null,
    onOpenFilter: (key, anchor) => setMaterialOpenFilter({ key, anchor }),
    onCloseFilter: () => setMaterialOpenFilter(null),
    onApplyFilter: (key, picked, all) => {
      setMaterialFilters((prev) => {
        const next = { ...prev };
        if (!picked.length || picked.length === all.length) delete next[key]; else next[key] = picked;
        return next;
      });
      setMaterialOpenFilter(null);
    },
  });

  const [materialBox, materialWin] = useRowWindow({
    rowHeight: 32,
    total: materialRows.length,
    resetKey: `${materialSort.key}|${materialSort.dir}|${materialType}|${materialSearch}|${Object.keys(materialFilters).join(',')}`,
  });

  const exportMaterialExcel = async () => {
    setXlsxBusy(true); setError('');
    try {
      const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
      const filename = `HEVN_Material_Master_${stamp.replace(/[: ]/g, '-')}.xlsx`;
      const active = Object.entries(materialFilters)
        .map(([key, values]) => {
          const col = MATERIAL_COLUMNS.find((c) => c.key === key);
          return `${col ? t(col.labelKey) : key}: ${[...values].join(', ')}`;
        });
      const res = await fetch(`${BACKEND_URL}/api/development/sample-tracker/export-xlsx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          filename,
          title: t('xHub.material.expTitle'),
          generated: stamp,
          sheetName: t('xHub.material.expSheet'),
          filters: [
            t('xHub.common.rowsOf', { n: materialRows.length, total: (material?.rows || []).length }),
            materialType ? t('xHub.material.typeFilter', { type: materialType }) : '',
            materialSearch.trim() ? t('xHub.common.search', { q: materialSearch.trim() }) : '',
            ...active,
          ].filter(Boolean),
          columns: MATERIAL_COLUMNS.map((c) => t(c.labelKey)),
          widths: MATERIAL_COLUMNS.map((c) => Math.max(10, Math.round(c.width / 7))),
          freezeColumns: 2,
          rows: materialRows.map((r) => ({ values: MATERIAL_COLUMNS.map((c) => r[c.key] ?? '') })),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || t('xHub.common.exportFailed', { status: res.status }));
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(e.message); } finally { setXlsxBusy(false); }
  };

  const [styleBox, styleWin] = useRowWindow({
    rowHeight: 32,
    total: styleRows.length,
    resetKey: `${styleSort.key}|${styleSort.dir}|${Object.keys(styleFilters).join(',')}`,
  });

  // Excel's rule: a column's own filter is ignored when building its list, so unticking a value does not make
  // it vanish from the list you unticked it in. distinctColumnValues already does this given the full set.
  const styleTh = (column) => ({
    label: column.label,
    sortKey: column.key,
    sort: styleSort,
    onSort: (key, dir) => setStyleSort((prev) => ({
      key,
      dir: dir || (prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'),
    })),
    filterValues: styleOpenFilter?.key === column.key
      ? distinctColumnValues(style?.rows || [], column.key, styleFilters, STYLE_GETTERS)
      : undefined,
    filterSelected: styleFilters[column.key],
    filterOpen: styleOpenFilter?.key === column.key,
    filterAnchor: styleOpenFilter?.key === column.key ? styleOpenFilter.anchor : null,
    onOpenFilter: (key, anchor) => setStyleOpenFilter({ key, anchor }),
    onCloseFilter: () => setStyleOpenFilter(null),
    onApplyFilter: (key, picked, all) => {
      setStyleFilters((prev) => {
        const next = { ...prev };
        // Everything ticked means no filter at all — storing the full list would mark the column as filtered
        // and, worse, freeze it against rows arriving later.
        if (!picked.length || picked.length === all.length) delete next[key]; else next[key] = picked;
        return next;
      });
      setStyleOpenFilter(null);
    },
  });

  const loadMaterial = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/material-master?page=1&pageSize=50000`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) setMaterial({ ...data, rows: withDerived(data.rows) });
    } catch (err) {
      // keep the previous result rather than blanking the table
    }
  }, []);

  useEffect(() => { if (tab === 'material') loadMaterial(); }, [tab, loadMaterial]);

  /**
   * Fetch one page. `append` false starts a fresh list (a filter changed); true adds to what is there.
   * The in-flight guard matters: a scroll event fires many times on the way down, and without it the same
   * page would be requested repeatedly and appended more than once.
   */
  const runManualRefresh = useCallback(async (kind) => {
    if (manual.step) return;
    // The label is only shown (and tested for being set) — never sent — so it is stored translated.
    const steps = kind === 'sap'
      ? [[i18n.t('xHub.refresh.stepSapPo'), `${API_BASE}/sap/import/po?apply=1`], [i18n.t('xHub.refresh.stepSapSo'), `${API_BASE}/sap/import/so?apply=1`]]
      : [[i18n.t('xHub.refresh.stepNsPo'), `${API_BASE}/netsuite/import`]];
    setManual({ kind, step: steps[0][0], error: '', done: '' });
    const summary = [];
    try {
      for (const [label, url] of steps) {
        setManual({ kind, step: label, error: '', done: '' });
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: kind === 'sap' ? undefined : JSON.stringify({}),
        });
        // eslint-disable-next-line no-await-in-loop
        const data = await response.json();
        if (response.status === 403) throw new Error(i18n.t('xHub.refresh.needsEdit'));
        if (!response.ok || data.ok === false) throw new Error(data.error || i18n.t('xHub.failedStatus', { status: response.status }));
        const st = data.stats || {};
        const added = st.insert ?? st.inserted ?? 0;
        const changed = st.update ?? st.updated ?? 0;
        summary.push(i18n.t('xHub.refresh.summary', { label, added, changed }));
      }
      setManual({ kind: '', step: '', error: '', done: summary.join(' · ') });
    } catch (err) {
      setManual({ kind: '', step: '', error: err.message, done: '' });
    }
    loadRefreshStatus();
  }, [manual.step, loadRefreshStatus]);

  const loadDocs = useCallback(async (query) => {
    try {
      const params = new URLSearchParams({ kind: query.kind, limit: '500' });
      if (query.year) params.set('year', query.year);
      if (query.q) params.set('q', query.q);
      if (query.missing) params.set('missing', '1');
      const response = await fetch(`${API_BASE}/ledger/documents?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) { setDocs(data); setDocSel([]); }
    } catch (err) {
      // keep whatever is on screen
    }
  }, []);

  useEffect(() => { if (tab === 'season') loadDocs(docQuery); }, [tab, docQuery, loadDocs]);

  const saveDocs = async () => {
    if (!docSel.length) return;
    setDocForm((prev) => ({ ...prev, saving: true, error: '', done: '' }));
    try {
      // Only the fields actually filled in are sent. An absent key leaves that column alone, so setting a
      // Buy# on a batch of documents cannot wipe a Season somebody already entered.
      const body = { kind: docQuery.kind, docNos: docSel };
      if (docForm.season !== '') body.season = docForm.season;
      if (docForm.buyCycle !== '') body.buyCycle = docForm.buyCycle;
      if (body.season === undefined && body.buyCycle === undefined) {
        throw new Error(t('xHub.season.typeFirst'));
      }
      const response = await fetch(`${API_BASE}/ledger/documents`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xHub.failedStatus', { status: response.status }));
      setDocForm({ season: '', buyCycle: '', saving: false, error: '',
        done: t('xHub.season.done', { docs: data.documents, lines: data.linesUpdated, logged: data.logged }) });
      loadDocs(docQuery);
    } catch (err) {
      setDocForm((prev) => ({ ...prev, saving: false, error: err.message }));
    }
  };

  /** Save one field of one document — the same endpoint the bulk bar uses, with a list of one. */
  const saveCell = async () => {
    if (!cellEdit) return;
    setCellEdit((prev) => ({ ...prev, saving: true, error: '' }));
    try {
      const body = { kind: docQuery.kind, docNos: [cellEdit.docNo] };
      body[cellEdit.field] = cellEdit.value;          // '' clears the field, which is a legitimate edit
      const response = await fetch(`${API_BASE}/ledger/documents`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('xHub.failedStatus', { status: response.status }));
      setCellEdit(null);
      loadDocs(docQuery);
    } catch (err) {
      setCellEdit((prev) => ({ ...prev, saving: false, error: err.message }));
    }
  };

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main">
        <div className="mh-titlerow">
        <div className="sp-title">
          {/* "Style Hub" since 2026-09-08 (Max). The function key stays `sp_hub`: renaming it would
              mean re-granting the screen to everyone who has it, and a permission key is not a label. */}
          <h2>{t('xShell.styleHub')}</h2>
        </div>
        <ModuleHelp steps={TOUR} tourKey="spHub" ready={!!conn || !!error} onBeforeTour={() => setTab('connections')} />
        </div>

        <div className="sp-tabs view-tabs" role="tablist">
          {TABS.map((item) => (
            <button
              type="button"
              key={item.key}
              className={`sp-tab ${tab === item.key ? 'active' : ''}`}
              onClick={() => setTab(item.key)}
            >
              <TabIcon k={item.key} />{t(item.labelKey)}
            </button>
          ))}
        </div>

        {tab === 'connections' && (
          <section className="sp-section">
            <div className="sp-section-head">
              <div>
                <Plug size={14} /> {t('xHub.src.head')}
                <span className="sp-sub">{t('xHub.src.lastChecked', { time: fmtTime(conn?.checkedAt) })}</span>
              </div>
              <button type="button" className="sp-btn" onClick={loadConnections} disabled={loading}>
                <RefreshCw size={13} /> {loading ? t('xHub.src.testing') : t('xHub.src.testAgain')}
              </button>
            </div>

            {error && <div className="sp-alert err">{error}</div>}

            {/* Without this the page contradicts itself: every card reads "not configured" while the refresh
                table below says the ledgers are up to date. Both are true — the integration credentials live
                on the staging backend, which is where the scheduled job runs, and this server holds none.
                Deliberately: one place holds the secrets and one path writes the ledgers. */}
            {conn && !conn.hasCredentials && (
              <div className="sp-alert">{t('xHub.src.noCreds')}</div>
            )}

            <div className="sp-conn-grid">
              {(conn?.sources || []).map((source) => (
                <ConnectionCard key={source.key} source={source} credentialsElsewhere={conn && !conn.hasCredentials} />
              ))}
            </div>

            {/* Says where the style and season data actually comes from, now that the PLM card is gone.
                Without it, dropping the card would just leave a silent hole where a source used to be. */}
            <p className="sp-note">
              <Trans t={t} i18nKey="xHub.src.plmNote" components={{ b: <strong />, c: <code /> }} />
            </p>

            {/* Below the connection cards on purpose: "can we reach SAP" and "when did the data last change"
                are different questions, and the second one is the one most people came for. */}
            <RefreshPanel
              status={refreshStatus}
              onReload={loadRefreshStatus}
              loading={refreshLoading}
              canRefresh={!!conn?.canViewLedger && !!conn?.hasCredentials}
              manual={manual}
              onRefreshSource={runManualRefresh}
            />

            {/* The NetSuite read-only preview was removed on 2026-08-27: it showed 25 raw SuiteQL rows
                from a page whose job is "is the connection alive", and the same lines are on Ledgers (raw)
                with sort, filter and every column. loadPreview and the endpoint behind it are untouched. */}
          </section>
        )}

        {/* Recon. This section was deleted by accident on 2026-08-27 while the SAP Connection Test and the
            NetSuite preview were being removed, which is why the tab rendered as a blank page — the state, the
            loader and both endpoints were still in place. Rebuilt to the conventions the other tables have
            since been given: the whole level in the browser, Excel headers, 15 rows and a scroll, no pager. */}
        {tab === 'style' && (
          <section className="sp-section">
            <div className="sp-section-head"><div><Database size={14} /> {t('xHub.style.head')}</div></div>
            {/* The column headings below stay as SAP prints them, so the note names them in English too. */}
            <p className="sp-note">
              <Trans t={t} i18nKey="xHub.style.note" components={{ b: <strong />, c: <code /> }} />
            </p>

            <div className="sp-toolbar">
              {/* The three toolbar filters are gone: the column menus do all of it, and the FG Code menu has
                  its own search box, so a free-text field above the table was a second way to do one thing.
                  The Season tick stays — it is the one filter that is a question about the data rather than
                  about a column, and it is what the PLM/SAP conversation is currently about. */}
              <label className="sp-check">
                <input
                  type="checkbox"
                  checked={styleQuery.noSeason}
                  onChange={(e) => setStyleQuery({ noSeason: e.target.checked })}
                />
                {t('xHub.style.noSeason')}
              </label>
              <span className="sp-sub">
                {styleRows.length !== (style?.rows || []).length
                  ? t('xHub.style.countOf', { n: styleRows.length.toLocaleString('en-GB'), total: (style?.rows || []).length.toLocaleString('en-GB') })
                  : t('xHub.style.countN', { n: styleRows.length.toLocaleString('en-GB') })}
              </span>
              <button
                type="button"
                className="sp-btn"
                disabled={xlsxBusy || !styleRows.length}
                onClick={exportStyleExcel}
              >
                <Download size={13} /> {xlsxBusy ? t('xHub.common.building') : t('xHub.common.exportExcel')}
              </button>
              {(!!Object.keys(styleFilters).length || !!styleSort.key) && (
                <button
                  type="button"
                  className="sp-btn"
                  onClick={() => { setStyleFilters({}); setStyleSort({ key: '', dir: 'asc' }); }}
                >
                  {t('xHub.common.clearTools')}
                </button>
              )}
            </div>

            <div className="sp-table-wrap sp-scroll15" ref={styleBox}>
              <table className="sp-table sp-table-fixed" style={{ minWidth: STYLE_TABLE_W }}>
                <colgroup>
                  <col style={{ width: STYLE_NO_W }} />
                  {STYLE_COLUMNS.map((c) => <col key={c.key} style={{ width: c.width }} />)}
                </colgroup>
                <thead>
                  <tr>
                    {/* Not a SortTh: the number is the row's position in what is on screen right now, so
                        sorting or filtering BY it would be circular. */}
                    <th className="sp-rownum">{t('xHub.common.rowNo')}</th>
                    {STYLE_COLUMNS.map((c) => <SortTh key={c.key} {...styleTh(c)} />)}
                  </tr>
                </thead>
                <tbody>
                  {/* Only the rows on screen — 10,707 of them took ~5 s and 134,696 DOM nodes to lay
                      out for a box 679px tall. The spacers stand in for the rest, so the scrollbar is
                      still the length of the whole list. */}
                  {styleWin.padTop > 0 && (
                    <tr aria-hidden="true"><td colSpan={STYLE_COLUMNS.length + 1} style={{ height: styleWin.padTop, padding: 0, border: 0 }} /></tr>
                  )}
                  {styleRows.slice(styleWin.first, styleWin.first + styleWin.count).map((row, sliceIndex) => {
                  // The number is the row's place in the whole list, not in the slice on screen.
                  const index = styleWin.first + sliceIndex;
                  return (
                    <tr key={`${row.style_pk_id}|${row.plm_season || ''}`}>
                      <td className="sp-rownum">{index + 1}</td>
                      <td>{row.style_key}</td>
                      <td>{row.sku || <span className="sp-muted">—</span>}</td>
                      {/* SAP's own text, Korean status notes such as "(생산금지)" included — not cleaned up,
                          because that note is the only place some styles say they are discontinued. */}
                      <td title={row.maktx || ''}>{row.maktx || '—'}</td>
                      <td title={row.line_name || ''}>{row.line_name || '—'}</td>
                      <td title={row.product_category || ''}>{row.product_category || '—'}</td>
                      <td>{row.material_type || <span className="sp-muted">—</span>}</td>
                      <td>{row.material_group || <span className="sp-muted">—</span>}</td>
                      <td title={row.color || ''}>{row.color || <span className="sp-muted">—</span>}</td>
                      <td>{row.ean || <span className="sp-muted">—</span>}</td>
                      <td>{row.plm_season || <span className="sp-muted">—</span>}</td>
                    </tr>
                  );
                  })}
                  {styleWin.padBottom > 0 && (
                    <tr aria-hidden="true"><td colSpan={STYLE_COLUMNS.length + 1} style={{ height: styleWin.padBottom, padding: 0, border: 0 }} /></tr>
                  )}
                  {!styleRows.length && <tr><td className="sp-empty" colSpan={STYLE_COLUMNS.length + 1}>{t('xHub.style.noMatch')}</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Season import. Only offered to someone who can edit the Hub — the endpoint refuses anyone
                else anyway, but a button that always fails is worse than no button. */}
            {conn?.canViewLedger && (
              <div className="sp-season-import">
                <div className="sp-section-head">
                  <div><Database size={14} /> {t('xHub.style.importHead')}</div>
                </div>
                <p className="sp-note">
                  <Trans t={t} i18nKey="xHub.style.importNote1" components={{ b: <strong />, c: <code /> }} />
                </p>
                <p className="sp-note">{t('xHub.style.importNote2')}</p>

                <div className="sp-toolbar">
                  <input
                    type="file"
                    className="sp-input"
                    accept=".xlsx,.csv"
                    onChange={(e) => pickSeasonFile(e.target.files?.[0] || null)}
                  />
                  <button
                    type="button"
                    className="sp-btn"
                    disabled={!seasonFile || seasonBusy}
                    onClick={() => runSeasonImport(false)}
                  >
                    {seasonBusy ? t('xHub.style.reading') : t('xHub.style.check')}
                  </button>
                  {!!seasonPreview?.willChange && !seasonPreview.applied && (
                    <button
                      type="button"
                      className="sp-btn primary"
                      disabled={seasonBusy}
                      onClick={() => runSeasonImport(true)}
                    >
                      {t('xHub.style.apply', { count: seasonPreview.willChange })}
                    </button>
                  )}
                </div>

                {seasonError && <div className="sp-alert err">{seasonError}</div>}

                {seasonPreview && (
                  <>
                    <div className={`sp-alert ${seasonPreview.applied ? 'ok' : ''}`}>
                      {seasonPreview.applied
                        ? t('xHub.style.applied', { n: seasonPreview.willChange, file: seasonPreview.fileName })
                        : t('xHub.style.read', {
                          n: seasonPreview.rowsRead.toLocaleString('en-GB'),
                          file: seasonPreview.fileName,
                          code: seasonPreview.columns.code,
                          season: seasonPreview.columns.season,
                        })}
                    </div>
                    <div className="sp-stat-row">
                      <Stat label={t('xHub.style.statWould')} value={seasonPreview.willChange} tone={seasonPreview.willChange ? undefined : 'warn'} />
                      <Stat label={t('xHub.style.statCorrect')} value={seasonPreview.unchanged} />
                      {/* Every one of these is a row the import will NOT act on, so each is named rather
                          than folded into a single "skipped" figure that hides which problem it was. */}
                      <Stat label={t('xHub.style.statNotFound')} value={seasonPreview.unmatched} tone={seasonPreview.unmatched ? 'warn' : undefined} />
                      <Stat label={t('xHub.style.statBlank')} value={seasonPreview.blankSeason} />
                      {/* Not a problem, and not hidden either: a product carried from one line-up into the
                          next has a row in each season, and only the latest is stored. */}
                      <Stat label={t('xHub.style.statMulti')} value={seasonPreview.multiSeason} sub={t('xHub.style.statMultiSub')} />
                    </div>

                    {!!seasonPreview.sampleChanges?.length && (
                      <div className="sp-table-wrap">
                        <table className="sp-table">
                          <thead><tr><th>{t('xHub.style.colSap')}</th><th>{t('xHub.style.colNow')}</th><th>{t('xHub.style.colFile')}</th></tr></thead>
                          <tbody>
                            {seasonPreview.sampleChanges.map((c) => (
                              <tr key={c.styleKey}>
                                <td>{c.styleKey}</td>
                                <td>{c.from || <span className="sp-muted">—</span>}</td>
                                <td><strong>{c.to}</strong></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {!!seasonPreview.sampleUnmatched?.length && (
                      <p className="sp-note">
                        <Trans t={t} i18nKey="xHub.style.notFound" values={{ list: seasonPreview.sampleUnmatched.join(', ') }} components={{ c: <code /> }} />
                      </p>
                    )}
                    {!!seasonPreview.sampleMultiSeason?.length && (
                      <p className="sp-note">
                        <Trans t={t} i18nKey="xHub.style.multi" values={{ list: seasonPreview.sampleMultiSeason.join(' | ') }} components={{ c: <code /> }} />
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        )}

        {tab === 'season' && (
          <section className="sp-section">
            <div className="sp-section-head">
              <div><Database size={14} /> {t('xHub.season.head')}</div>
              <button type="button" className="sp-btn" onClick={() => loadDocs(docQuery)}>
                <RefreshCw size={13} /> {t('xHub.common.refresh')}
              </button>
            </div>
            <p className="sp-note">
              <Trans t={t} i18nKey="xHub.season.note1" components={{ b: <strong /> }} />
            </p>
            <p className="sp-note">
              <Trans t={t} i18nKey="xHub.season.note2" components={{ b: <strong /> }} />
            </p>

            <div className="sp-hub-bar">
              <select className="sp-input" value={docQuery.kind}
                onChange={(event) => setDocQuery((prev) => ({ ...prev, kind: event.target.value }))}>
                <option value="po">{t('xHub.season.sapPo')}</option>
                <option value="ns">{t('xHub.season.nsPo')}</option>
              </select>
              <select className="sp-input" value={docQuery.year}
                onChange={(event) => setDocQuery((prev) => ({ ...prev, year: event.target.value }))}>
                <option value="">{t('xHub.season.allYears')}</option>
                {['2026', '2025', '2024', '2023'].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <input className="sp-input" placeholder={t('xHub.season.searchPh')} value={docQuery.q}
                onChange={(event) => setDocQuery((prev) => ({ ...prev, q: event.target.value }))} />
              <label className="sp-check">
                <input type="checkbox" checked={docQuery.missing}
                  onChange={(event) => setDocQuery((prev) => ({ ...prev, missing: event.target.checked }))} />
                {t('xHub.season.missing')}
              </label>
              <span className="sp-sub">{t('xHub.season.docs', { n: (docs?.rows?.length ?? 0).toLocaleString('en-GB') })}</span>
            </div>

            <div className="sp-hub-bar">
              <input className="sp-input" placeholder={t('xHub.season.seasonPh')} value={docForm.season}
                onChange={(event) => setDocForm((prev) => ({ ...prev, season: event.target.value }))} />
              <input className="sp-input" placeholder={t('xHub.season.buyPh')} value={docForm.buyCycle}
                onChange={(event) => setDocForm((prev) => ({ ...prev, buyCycle: event.target.value }))} />
              <button type="button" className="sp-btn" disabled={!docSel.length || docForm.saving} onClick={saveDocs}>
                {docForm.saving ? t('xHub.common.saving') : t('xHub.season.applyTo', { n: docSel.length })}
              </button>
              {docForm.error && <span className="sp-pill err">{docForm.error}</span>}
              {docForm.done && <span className="sp-pill ok">{docForm.done}</span>}
            </div>

            <div className="sp-table-wrap">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={!!docs?.rows?.length && docSel.length === docs.rows.length}
                        onChange={(event) => setDocSel(event.target.checked ? (docs?.rows || []).map((r) => r.doc_no) : [])}
                      />
                    </th>
                    <th>{t('xHub.season.colDoc')}</th><th>{t('xHub.season.colDate')}</th>
                    <th>{t('xHub.season.colSupplier')}</th><th>{t('xHub.season.colLines')}</th>
                    <th className="sp-col-edit">{t('xHub.season.colSeason')}</th>
                    <th className="sp-col-edit">{t('xHub.season.colBuy')}</th><th>{t('xHub.season.colPlm')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(docs?.rows || []).map((row) => {
                    // MIN and MAX differing means the document's own lines disagree — surfaced rather than hidden.
                    const seasonMixed = row.season_min !== row.season_max;
                    const buyMixed = row.buy_min !== row.buy_max;
                    const sugMixed = row.suggested_season_min !== row.suggested_season_max;
                    return (
                      <tr key={row.doc_no} className={!row.season_min || !row.buy_min ? 'sp-row-gap' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={docSel.includes(row.doc_no)}
                            onChange={(event) => setDocSel((prev) => (event.target.checked
                              ? [...prev, row.doc_no]
                              : prev.filter((d) => d !== row.doc_no)))}
                          />
                        </td>
                        <td>{row.doc_no}</td>
                        <td>{row.doc_date ? String(row.doc_date).slice(0, 10) : '—'}</td>
                        <td>{row.supplier || '—'}</td>
                        <td>{Number(row.lines).toLocaleString('en-GB')}</td>
                        {[['season', row.season_min, row.season_max, seasonMixed, 'S26'],
                          ['buyCycle', row.buy_min, row.buy_max, buyMixed, 'Buy#2 SLT']].map(
                          ([field, min, max, mixed, hint]) => {
                            const editing = cellEdit?.docNo === row.doc_no && cellEdit?.field === field;
                            if (editing) {
                              return (
                                <td key={field} className="sp-col-edit">
                                  <input
                                    className="sp-cell-input"
                                    autoFocus
                                    placeholder={hint}
                                    value={cellEdit.value}
                                    onChange={(event) => setCellEdit((prev) => ({ ...prev, value: event.target.value }))}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') saveCell();
                                      if (event.key === 'Escape') setCellEdit(null);
                                    }}
                                    // Blur saves too: clicking straight onto the next cell is the natural way
                                    // to work down a column, and losing the typed value there would be brutal.
                                    onBlur={() => { if (!cellEdit.saving) saveCell(); }}
                                  />
                                  {cellEdit.error && <span className="sp-cell-err">{cellEdit.error}</span>}
                                </td>
                              );
                            }
                            return (
                              <td
                                key={field}
                                className="sp-cell-edit sp-col-edit"
                                title={t('xHub.season.editTitle')}
                                onClick={() => setCellEdit({
                                  docNo: row.doc_no, field, value: mixed ? '' : (min || ''), saving: false, error: '',
                                })}
                              >
                                {mixed
                                  ? <span className="sp-pill err">{t('xHub.season.mixed', { a: min || '—', b: max || '—' })}</span>
                                  : (min || <span className="sp-sub">—</span>)}
                              </td>
                            );
                          })}
                        <td className="sp-sub">{sugMixed
                          ? `${row.suggested_season_min || '—'} / ${row.suggested_season_max || '—'}`
                          : (row.suggested_season_min || '—')}</td>
                      </tr>
                    );
                  })}
                  {!docs?.rows?.length && <tr><td className="sp-empty" colSpan={8}>{t('xHub.season.none')}</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === 'material' && (
          <section className="sp-section">
            <div className="sp-section-head">
              <div><Database size={14} /> {t('xHub.material.head')}</div>
              <button type="button" className="sp-btn" onClick={() => loadMaterial()}>
                <RefreshCw size={13} /> {t('xHub.common.refresh')}
              </button>
            </div>

            {/* Said out loud: this is an export loaded once, not a live feed. A stale lookup that looks
                live is worse than an obviously old one. */}
            {material?.source && (
              <p className="sp-sub">
                <Trans
                  t={t}
                  i18nKey="xHub.material.snapshot"
                  values={{
                    file: material.source.source_file,
                    date: new Date(material.source.imported_at).toLocaleDateString('en-GB'),
                  }}
                  components={{ c: <code /> }}
                />
              </p>
            )}

            <div className="sp-toolbar">
              {/* No Enter to submit any more — the whole table is in the browser, so it filters as you
                  type instead of making a round trip per keystroke. */}
              <input
                className="sp-input"
                style={{ minWidth: 260 }}
                placeholder={t('xHub.material.searchPh')}
                value={materialSearch}
                onChange={(e) => setMaterialSearch(e.target.value)}
              />
              <select
                className="sp-input"
                value={materialType}
                onChange={(e) => setMaterialType(e.target.value)}
              >
                <option value="">{t('xHub.material.allTypes')}</option>
                {(material?.types || []).map((mt) => (
                  <option key={mt.material_type} value={mt.material_type}>
                    {mt.material_type} ({mt.n.toLocaleString('en-GB')})
                  </option>
                ))}
              </select>
              <span className="sp-sub">
                {materialRows.length !== (material?.rows || []).length
                  ? t('xHub.common.rowsOf', { n: materialRows.length.toLocaleString('en-GB'), total: (material?.rows || []).length.toLocaleString('en-GB') })
                  : t('xHub.common.rowsN', { n: materialRows.length.toLocaleString('en-GB') })}
              </span>
              {(!!Object.keys(materialFilters).length || !!materialSort.key) && (
                <button
                  type="button"
                  className="sp-btn"
                  onClick={() => { setMaterialFilters({}); setMaterialSort({ key: '', dir: 'asc' }); }}
                >
                  {t('xHub.common.clearTools')}
                </button>
              )}
              <button
                type="button"
                className="sp-btn"
                disabled={xlsxBusy || !materialRows.length}
                onClick={exportMaterialExcel}
              >
                <Download size={13} /> {xlsxBusy ? t('xHub.common.building') : t('xHub.common.exportExcel')}
              </button>
            </div>

            <div className="sp-table-wrap sp-scroll15" ref={materialBox}>
              <table className="sp-table sp-table-fixed" style={{ minWidth: MATERIAL_TABLE_W }}>
                <colgroup>
                  <col style={{ width: MATERIAL_NO_W }} />
                  {MATERIAL_COLUMNS.map((c) => <col key={c.key} style={{ width: c.width }} />)}
                </colgroup>
                <thead>
                  <tr>
                    {/* Not a SortTh: the number is the row's position in what is on screen right now, so
                        sorting or filtering BY it would be circular. */}
                    <th className="sp-rownum">{t('xHub.common.rowNo')}</th>
                    {MATERIAL_COLUMNS.map((c) => <SortTh key={c.key} {...materialTh(c)} />)}
                  </tr>
                </thead>
                <tbody>
                  {/* Only the rows on screen; the spacers stand in for the rest so the scrollbar is still
                      the length of all 15,026. */}
                  {materialWin.padTop > 0 && (
                    <tr aria-hidden="true"><td colSpan={MATERIAL_COLUMNS.length + 1} style={{ height: materialWin.padTop, padding: 0, border: 0 }} /></tr>
                  )}
                  {materialRows.slice(materialWin.first, materialWin.first + materialWin.count).map((row, sliceIndex) => (
                    <tr key={row.material_code}>
                      <td className="sp-rownum">{materialWin.first + sliceIndex + 1}</td>
                      <td>{row.material_code}</td>
                      <td>{row.material_type || '—'}</td>
                      <td title={row.name_en || ''}>{row.name_en || '—'}</td>
                      <td title={row.line_category || ''}>{row.line_category || '—'}</td>
                      <td>{row.legacy_no || '—'}</td>
                      <td>{row.plm_material_no || '—'}</td>
                      <td>{row.base_uom || '—'}</td>
                      <td>{row.ean || '—'}</td>
                    </tr>
                  ))}
                  {materialWin.padBottom > 0 && (
                    <tr aria-hidden="true"><td colSpan={MATERIAL_COLUMNS.length + 1} style={{ height: materialWin.padBottom, padding: 0, border: 0 }} /></tr>
                  )}
                  {!materialRows.length && <tr><td className="sp-empty" colSpan={MATERIAL_COLUMNS.length + 1}>{t('xHub.material.noMatch')}</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        )}

      </div>
    </div>
  );
}
